import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { SHIFTS, SKILLS, MACHINES, SHIFT_COLORS } from '../../data/operatorConstants.js';

const defaultForm = {
  name: '',
  shift: 'A',
  skills: [],
  preferredMachines: [],
  active: true,
  notes: ''
};

const OperatorFormModal = ({ show, onHide, operator, onSubmit, isLoading }) => {
  const isEdit = !!operator;
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (operator) {
      setForm({
        name: operator.name || '',
        shift: operator.shift || 'A',
        skills: operator.skills || [],
        preferredMachines: operator.preferredMachines || [],
        active: operator.active !== undefined ? operator.active : true,
        notes: operator.notes || ''
      });
    } else {
      setForm(defaultForm);
    }
  }, [operator, show]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleSkill = skill => {
    setForm(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const toggleMachine = machine => {
    setForm(prev => ({
      ...prev,
      preferredMachines: prev.preferredMachines.includes(machine)
        ? prev.preferredMachines.filter(m => m !== machine)
        : [...prev.preferredMachines, machine]
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (isEdit) {
      onSubmit({ id: operator._id, ...form });
    } else {
      onSubmit(form);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? 'Edit Operator' : 'Add Operator'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Operator Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter operator name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Shift</Form.Label>
                <div className="d-flex gap-3 mt-1">
                  {SHIFTS.map(shift => (
                    <Form.Check
                      key={shift}
                      type="radio"
                      id={`shift-${shift}`}
                      name="shift"
                      label={
                        <Badge bg={SHIFT_COLORS[shift]}>
                          Shift {shift}
                        </Badge>
                      }
                      value={shift}
                      checked={form.shift === shift}
                      onChange={handleChange}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Skills</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {SKILLS.map(skill => (
                <Badge
                  key={skill.key}
                  bg={form.skills.includes(skill.key) ? 'info' : 'light'}
                  text={form.skills.includes(skill.key) ? 'white' : 'dark'}
                  style={{
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    border: '1px solid #dee2e6'
                  }}
                  onClick={() => toggleSkill(skill.key)}
                >
                  {skill.label}
                </Badge>
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Preferred Machines</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {MACHINES.map(machine => (
                <Badge
                  key={machine}
                  bg={
                    form.preferredMachines.includes(machine)
                      ? 'primary'
                      : 'light'
                  }
                  text={
                    form.preferredMachines.includes(machine) ? 'white' : 'dark'
                  }
                  style={{
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    border: '1px solid #dee2e6'
                  }}
                  onClick={() => toggleMachine(machine)}
                >
                  {machine}
                </Badge>
              ))}
            </div>
          </Form.Group>

          {isEdit && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="active-switch"
                label="Active Operator"
                checked={form.active}
                onChange={e =>
                  setForm(prev => ({ ...prev, active: e.target.checked }))
                }
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Additional notes (optional)"
              maxLength={500}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading
              ? 'Saving...'
              : isEdit
                ? 'Save'
                : 'Add'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default OperatorFormModal;
